/**
 * Of de database onnodig werk doet, in woorden.
 *
 * ------------------------------------------------------------------
 * De grens en de tekst staan hier en niet in de JSX, om dezelfde reden als bij
 * syncAge.js: een getal zonder oordeel is een getal waar iedereen zelf een
 * verhaal bij verzint, en een oordeel in een scherm is een oordeel dat niemand
 * kan natrekken.
 *
 * Wat gemeten wordt: de kaart levert ruim duizend meldingen, en Mongo heeft
 * daarvoor een zeker aantal documenten moeten lezen. Dat getal komt uit
 * `GET /api/admin/explain/map` — de server voert de `explain("executionStats")`
 * zelf uit, want die vraagt de productie-connectiestring en die hoort nergens
 * buiten de omgeving van de server.
 *
 * Aanleiding: na het weghalen van de kwadratische clusterlus op 5 oktober
 * (1,95 s → 0,055 s bij n=5.000) is het plafond geen Python meer maar deze
 * query. Zie docs/performance-collaboration.md.
 */

/**
 * Hoeveel ruimer een `$geoNear` mag lezen dan hij teruggeeft.
 *
 * Twee, en dat is geen voorzichtige slag in de lucht: een `$geoNear` werkt van
 * binnen naar buiten in ringen en moet de buitenste ring altijd deels
 * wegstrepen om zeker te weten dat hij niets binnen de straal mist. Iets meer
 * lezen dan leveren is dus de werking en niet een fout.
 *
 * Daarboven wordt het een ander verhaal: zes keer lezen voor één rij betekent
 * dat de index de meeste kandidaten pas ná het ophalen afwijst, en dan staat de
 * winst in de index en niet in de code.
 */
export const RUIM = 2;

/**
 * `{ tekst, teBreed }` voor een antwoord van /api/admin/explain/map.
 *
 * `null` in plaats van een oordeel als de getallen niet compleet terugkwamen.
 * Dat gebeurt bij een andere Mongo-versie die de velden anders noemt, en dan is
 * "geen oordeel" eerlijker dan een oordeel over een ontbrekend getal.
 */
export function leeslast(uitleg) {
  const per = uitleg?.gelezen_per_geleverd;
  if (typeof per !== "number" || !Number.isFinite(per)) {
    return { tekst: "Geen oordeel: de getallen kwamen niet compleet terug.", teBreed: false };
  }
  if (per > RUIM) {
    return {
      teBreed: true,
      tekst: `Te breed. ${per}× meer gelezen dan geleverd — hier valt tijd te halen met een index.`,
    };
  }
  return {
    teBreed: false,
    tekst: `In orde. ${per}× is wat een $geoNear nodig heeft; hij werkt in ringen van binnen naar buiten.`,
  };
}

/**
 * Welke index gebruikt is, of de mededeling dat er geen was.
 *
 * Geen index is het interessantste antwoord dat dit endpoint kan geven, en het
 * zou het stilste zijn: een leeg veld leest als "niets te melden". Vandaar een
 * zin in plaats van niets.
 *
 * **Maar drie gevallen en niet twee**, en dat verschil is een reparatie van
 * 6 oktober. Op het scherm stond "Geen index gebruikt — dat is de eerste plek
 * om te kijken" terwijl er in werkelijkheid helemaal geen getallen waren
 * teruggekomen: de server vond zijn eigen `executionStats` niet. Dat is een
 * stellige bewering over de database op grond van een fout in onze eigen
 * uitlezing, en dat is erger dan zwijgen — iemand gaat dan een index bouwen
 * voor een probleem dat niet bestaat.
 *
 * `gevonden_in` zegt waar de server de getallen vond. Is dat leeg, dan weten we
 * niets over de index en hoort er ook niets beweerd te worden.
 */
export function indexRegel(uitleg) {
  const naam = uitleg?.plan?.inputStage?.indexName || uitleg?.plan?.indexName;
  if (naam) return ` Index: ${naam}.`;
  if (!uitleg?.gevonden_in) {
    return " Over de index valt niets te zeggen: de meting zelf kwam niet rond.";
  }
  return " Geen index gebruikt — dat is de eerste plek om te kijken.";
}

/**
 * Waaruit het weggegooide werk bestaat, en welke reparatie het wegneemt.
 *
 * Het tienvoud alleen zegt dát er verspild wordt, niet waaraan. En dat verschil
 * is geen detail maar twee heel verschillende klussen.
 *
 * Een `partialFilterExpression` op een MongoDB-index kan alleen gelijkheden en
 * bereiken aan. `active: true` mag erin; `lifecycle_status: {$nin: [...]}` en de
 * vervaltijdvoorwaarde (een `$not`) niet. Dus:
 *
 *   actief ≈ geschikt  → het dode gewicht is `active: false`, en een deelindex
 *                        op dat ene veld neemt het weg. Een kwartier werk.
 *   actief ≈ binnen    → het dode gewicht is verlopen of beëindigd, en dan
 *                        helpt zo'n deelindex niets. Dan is er een eigen veld
 *                        nodig dat "hoort op de kaart" bijhoudt, gezet door
 *                        dezelfde code die de vervaltijd zet.
 */
export function samenstelling(uitleg) {
  const s = uitleg?.samenstelling;
  if (!s || typeof s.binnen_straal !== "number") return null;
  const nl = (n) => n.toLocaleString("nl-NL");
  const kop = `Binnen de straal staan ${nl(s.binnen_straal)} meldingen, waarvan ${nl(s.actief)} actief en ${nl(s.geschikt)} geschikt voor de kaart.`;

  const dood = s.binnen_straal - s.geschikt;
  if (dood <= 0) return `${kop} Er wordt niets weggegooid.`;

  // Welke kant het op valt. De helft als scheidslijn: valt meer dan de helft
  // van het dode gewicht al weg op `active`, dan is dat ene veld de oplossing.
  const dooreenActief = s.binnen_straal - s.actief;
  return dooreenActief > dood / 2
    ? `${kop} Het meeste dode gewicht (${nl(dooreenActief)}) staat op "niet actief" — een deelindex op dat veld neemt het weg.`
    : `${kop} Het dode gewicht is vooral verlopen of beëindigd (${nl(s.actief - s.geschikt)}), niet "niet actief" — een deelindex op dat ene veld helpt daar niet. Daar is een eigen veld voor nodig.`;
}

/**
 * Wat het alternatief kost, naast wat het nu kost.
 *
 * `$geoNear` breidt uit in schillen en bekijkt documenten meermaals: gemeten op
 * 6 oktober las hij 37.076 documenten terwijl er 17.651 binnen de straal lagen.
 * `$geoWithin` is een gewone veldvoorwaarde zonder die overhead, en hij werkt
 * wél samen met een deelindex. In dit project bracht die wissel de feed van
 * 99 s naar 2,5 s.
 *
 * Of hij hier ook helpt is een vraag en geen aanname, dus staat het er als twee
 * getallen naast elkaar in plaats van als advies.
 */
export function vergelijking(uitleg) {
  const v = uitleg?.vergelijking;
  if (!v?.geoNear || !v?.geoWithin) return null;
  const near = v.geoNear.ms;
  const within = v.geoWithin.ms;
  const regel = `Zelfde werk met $geoNear: ${near} ms (${v.geoNear.n} documenten). Met $geoWithin: ${within} ms (${v.geoWithin.n}).`;
  if (!within || !near) return regel;
  if (within <= near / 1.5) {
    return `${regel} Dat is ${(near / within).toFixed(1)}× sneller — de wissel is het waard.`;
  }
  if (near <= within / 1.5) {
    return `${regel} $geoNear wint hier; de wissel zou het erger maken.`;
  }
  return `${regel} Nauwelijks verschil — de winst zit dan in de index en niet in de stage.`;
}
